import { AssetStateRecord } from './assetStateRecord';
import { z } from 'zod';

export type AssetState = z.infer<typeof AssetState>;
export type ChargingControllerChargerPrefix = z.infer<typeof ChargingControllerChargerPrefix>;
type _State = z.infer<typeof _State>;

export const ChargingControllerChargerPrefix = z
    .string()
    .refine(
        (value): value is `chargingController_charger_${number}${'' | '_connected' | '_shortcircuit' | '_voltage'}` =>
            value.match(/^chargingController_charger_\d+(|_connected|_shortcircuit|_voltage)$/) !== null,
    );

export const _State = z
    .object({
        lastSeen: AssetStateRecord(z.coerce.date()),
        cart_batteryPercent: AssetStateRecord(z.number()),
        cart_batteryLevel: AssetStateRecord(z.enum(['FULL', 'NORMAL', 'LOW', 'UNKNOWN'])),
        cart_batteryChargeOn: AssetStateRecord(z.coerce.date()),

        chargingController_all_chargers_enabled: AssetStateRecord(z.number()),
        chargingController_appVersion: AssetStateRecord(z.string()),
        chargingController_cart_count: AssetStateRecord(z.number()),
        chargingController_charger_type: AssetStateRecord(z.string()),
        chargingController_connector_type: AssetStateRecord(z.string()),
        chargingController_disabled_for_minutes: AssetStateRecord(z.number()),
        chargingController_executionTime: AssetStateRecord(z.number()),
        chargingController_heater_connected: AssetStateRecord(z.number()),
        chargingController_heater_enabled: AssetStateRecord(z.number()),
        chargingController_memoryUsage: AssetStateRecord(z.number()),
        chargingController_message: AssetStateRecord(z.string()),
        chargingController_off_in_the_morning: AssetStateRecord(z.number()),
        chargingController_short_circuit_detector_running: AssetStateRecord(z.number()),
        chargingController_shortcircuit_present: AssetStateRecord(z.number()),
        chargingController_startTime: AssetStateRecord(z.string()),
        chargingController_start_endpoint_called: AssetStateRecord(z.number()),
        chargingController_stop_endpoint_called: AssetStateRecord(z.number()),
        chargingController_switch_cabinet_count: AssetStateRecord(z.number()),
        chargingController_temperature: AssetStateRecord(z.number()),
        chargingController_temperature_sensor_connected: AssetStateRecord(z.number()),
        chargingController_threadCount: AssetStateRecord(z.number()),
    })
    .catchall(AssetStateRecord(z.unknown()));

export const AssetState = _State.transform(
    (state) =>
        Object.fromEntries(
            Object.entries(state).map(([key, value]) => {
                if (!ChargingControllerChargerPrefix.safeParse(key).success) {
                    return [key, value];
                }

                return [key, AssetStateRecord(z.number()).parse(value)];
            }),
        ) as _State & Record<ChargingControllerChargerPrefix, AssetStateRecord<number>>,
);
